// Defines initiative fields' default value and validation.

import { isValidTargetPeriod } from "../utils/targetPeriod";

const nonEmptyString = (value) =>
  typeof value === "string" && value.trim() !== "";

const boundedString = (max) => (value) =>
  nonEmptyString(value) && value.length <= max;

const nonEmptyArray = (value) => Array.isArray(value) && value.length > 0;

const numberInRange = (min, max) => (value) => {
  if (value === "" || value === null || value === undefined) return false;
  const n = Number(value);
  return Number.isFinite(n) && n >= min && n <= max;
};

export const INITIATIVE_FIELDS = {
  title: {
    label: "Title",
    default: "",
    required: true,
    maxLength: 100,
    type: "text",
    validate: boundedString(100),
  },
  description: {
    label: "Description",
    default: "",
    required: true,
    maxLength: 1000,
    type: "text",
    validate: boundedString(1000),
  },
  owner: {
    label: "Owner",
    default: "",
    required: true,
    maxLength: 100,
    type: "text",
    validate: boundedString(100),
  },
  horizon: {
    label: "Horizon",
    default: "",
    required: true,
    type: "string",
    validate: nonEmptyString,
  },
  status: {
    label: "Status",
    default: "",
    required: true,
    type: "string",
    validate: nonEmptyString,
  },
  priority: {
    label: "Priority",
    default: "",
    required: true,
    type: "string",
    validate: nonEmptyString,
  },
  targetPeriod: {
    label: "Target Period",
    default: "",
    required: false,
    type: "string",
    validate: isValidTargetPeriod,
  },
  estimate: {
    label: "Estimate",
    default: 1,
    required: true,
    min: 1,
    max: 4,
    type: "number",
    validate: numberInRange(1, 4),
  },
  progress: {
    label: "Progress",
    default: 0,
    required: true,
    min: 0,
    max: 100,
    type: "number",
    validate: numberInRange(0, 100),
  },
  product: {
    label: "Product",
    default: [],
    required: true,
    type: "stringArray",
    validate: nonEmptyArray,
  },
  impactAreas: {
    label: "Impact Areas",
    default: [],
    required: false,
    type: "stringArray",
  },
  gitlabEpic: {
    label: "GitLab Epic",
    default: [],
    required: false,
    type: "stringArray",
  },
  designLink: {
    label: "Design Link",
    default: [],
    required: false,
    type: "stringArray",
  },
  archived: {
    label: "Archived",
    default: false,
    required: false,
    type: "boolean",
  },
};

const FIELD_KEYS = Object.keys(INITIATIVE_FIELDS);

/*
 * Normalizes a single field's already-typed value (trims strings, coerces
 * numeric strings, trims/drops empty entries from string arrays) so every
 * caller shapes a field's value the same way instead of each reimplementing
 * it. A blank number is left as "" rather than defaulted, so validation (not
 * this step) is what rejects a missing required number.
 */
const trimString = (value) => value.trim();

const NORMALIZE_BY_TYPE = {
  text: trimString,
  string: trimString,
  number: (value) => (value === "" ? value : Number(value)),
  stringArray: (value) => value.map((v) => v.trim()).filter(Boolean),
};

export const normalizeField = (key, value) => {
  const normalize = NORMALIZE_BY_TYPE[INITIATIVE_FIELDS[key].type];
  return normalize ? normalize(value) : value;
};

// Normalizes every INITIATIVE_FIELDS key present on item; other properties (id, comments, ...) pass through untouched.
export const normalizeItem = (item) => {
  const normalized = { ...item };
  for (const key of FIELD_KEYS) {
    if (key in item) normalized[key] = normalizeField(key, item[key]);
  }
  return normalized;
};

// Builds a blank/default draft, applying any provided seed values.
export const createDraft = (seed = {}) => {
  const draft = {};
  for (const key of FIELD_KEYS) {
    const value = seed[key];
    draft[key] =
      value !== undefined && value !== null
        ? value
        : INITIATIVE_FIELDS[key].default;
  }
  draft.comments = seed.comments || [];
  return draft;
};

/*
 * A required field must pass its validator.
 * An optional field with a validator (e.g. targetPeriod's format check) must pass it when set.
 * An optional field with no validator at all (e.g. impactAreas/gitlabEpic) is always fine.
 */
export const isItemComplete = (item) => {
  return FIELD_KEYS.every((key) => {
    const field = INITIATIVE_FIELDS[key];
    if (field.required) return field.validate(item[key]);
    return field.validate ? field.validate(item[key]) : true;
  });
};
